import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	filterMap,
	Heading,
	HeadingLevel,
	If,
	Section,
	StyleProps,
} from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	FieldIf,
	FieldSet,
	Form,
	FormState,
	FormSubmitFunction,
	RadioField,
	SubFormField,
	SubFormRenderProps,
	useInternalField,
	useInternalFields,
	useOnSuccess,
} from "@hex-insights/forms";
import { FieldToggle } from "../../../Components/FieldToggle";
import {
	AddressFormValues,
	GuardianCustomFormState,
	GuardianCustomFormValues,
	PersonCustomFormValues,
	RelationshipFormValues,
} from "../../FormUtilities";
import { PersonField } from "../Person";
import { RelationshipField } from "../Relationship";
import { BaseFieldProps, BaseFormProps } from "../shared";

export type ControlledCreateInWizardProps = Omit<CreateInWizardProps, "formState"> & {
	formStateArgs?: GuardianCustomFormState.UseCreateFormStateArgs;
};

export function ControlledCreateInWizard({ formStateArgs, ...props }: ControlledCreateInWizardProps) {
	const formState = GuardianCustomFormState.useCreateFormState(formStateArgs);

	return <CreateInWizard formState={formState} {...props} />;
}

export type CreateInWizardProps = {
	formState: FormState<GuardianCustomFormValues.Create>;
	onSubmit: FormSubmitFunction<GuardianCustomFormValues.Create>;
	onSuccess: (formState: FormState<GuardianCustomFormValues.Create>) => void;
	formNameSuffix?: string;
	otherGuardians: GuardianCustomFormValues.Create[];
} & BaseFormProps<GuardianCustomFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Guardian model using the given form state.
 */
export function CreateInWizard({
	formState,
	onSubmit,
	onSuccess,
	formNameSuffix,
	otherGuardians,
	...props
}: CreateInWizardProps) {
	const formName = "guardian.create.wizard" + (formNameSuffix ? "." + formNameSuffix : "");

	useOnSuccess(formState, onSuccess);

	useInternalField(formState, "formIdentifier");
	useInternalField(formState, "relationshipStatus");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<PersonSubForm formState={formState} otherGuardians={otherGuardians} />
				<Section>
					<Section.Header>
						<Heading.H3 noMargin>Relationship with Student</Heading.H3>
					</Section.Header>
					<Section.Body>
						<RelationshipSubForm formState={formState} />
					</Section.Body>
				</Section>
			</Form.Column>
		</Form>
	);
}

function PersonSubForm({
	formState,
	noHeader = true,
	otherGuardians,
	style,
	...props
}: BaseFieldProps<Pick<GuardianCustomFormValues.Create, "person">> & {
	otherGuardians: GuardianCustomFormValues.Create[];
}) {
	const WrappedSubForm = React.useCallback(
		(props: SubFormRenderProps<PersonCustomFormValues.CreateInGuardian>) => (
			<PersonSubForm.SubForm {...props} otherGuardians={otherGuardians} />
		),
		[otherGuardians],
	);

	return (
		<SubFormField
			formState={formState}
			name="person"
			blankItem={PersonCustomFormValues.blankItemInGuardian}
			minItems={1}
			maxItems={1}
			noHeader={noHeader}
			style={{ "--sub-form-field__value---padding": 0, width: "100%", ...style } as React.CSSProperties}
			{...props}
		>
			{WrappedSubForm}
			{PersonSubForm.AddButton}
		</SubFormField>
	);
}

PersonSubForm.SubForm = function ({
	formState,
	onRemoveClick,
	canRemoveItem,
	otherGuardians,
}: SubFormRenderProps<PersonCustomFormValues.CreateInGuardian> & {
	otherGuardians: GuardianCustomFormValues.Create[];
}) {
	return (
		<Form.Column>
			<If condition={canRemoveItem}>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Person
				</Button>
			</If>
			<PersonCustomCreateFieldsInGuardian formState={formState} otherGuardians={otherGuardians} />
		</Form.Column>
	);
};

PersonSubForm.AddButton = function ({
	onClick,
	disabled,
	canAddItem,
}: AddSubFormButtonRenderProps<
	Pick<GuardianCustomFormValues.Create, "person">,
	PersonCustomFormValues.CreateInGuardian
>) {
	return (
		<If condition={canAddItem}>
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Person
			</Button>
		</If>
	);
};

const personInternalFields: (keyof PersonCustomFormValues.CreateInGuardian)[] = ["employeeID", "studentID"];

type PersonCustomCreateFieldsInGuardianProps = {
	formState: FormState<PersonCustomFormValues.CreateInGuardian>;
	headingLevel?: HeadingLevel;
	otherGuardians: GuardianCustomFormValues.Create[];
};

function PersonCustomCreateFieldsInGuardian({
	formState,
	headingLevel = 3,
	otherGuardians,
}: PersonCustomCreateFieldsInGuardianProps) {
	useInternalField(formState, "publicNameFormat");
	useInternalFields(formState, personInternalFields);

	const { addresses: setAddresses } = formState.formSetFunctions;
	const prevAddressSameAsFormIdentifier = React.useRef(formState.formValues.addressSameAsFormIdentifier);
	React.useEffect(() => {
		if (formState.formValues.addressSameAsFormIdentifier !== null) {
			setAddresses([]);
		} else if (
			formState.formValues.addressSameAsFormIdentifier === null &&
			prevAddressSameAsFormIdentifier.current !== null
		) {
			setAddresses((prev) => (prev.length === 0 ? [AddressFormValues.blankItemInPerson] : prev));
		}
		prevAddressSameAsFormIdentifier.current = formState.formValues.addressSameAsFormIdentifier;
	}, [formState.formValues.addressSameAsFormIdentifier, setAddresses]);

	return (
		<React.Fragment>
			<Section>
				<Section.Body style={{ paddingTop: 0 }}>
					<Form.Column>
						<PersonField.GivenName formState={formState} />
						<PersonField.MiddleName formState={formState} />
						<PersonField.FamilyName formState={formState} />
						<PersonField.NameOrder formState={formState} />
						<FieldToggle
							formState={formState}
							name="nickname"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Nickname"
							initialIsOpen={formState.formValues.nickname !== ""}
						>
							<PersonField.Nickname formState={formState} />
						</FieldToggle>
						<FieldToggle
							formState={formState}
							name="alternateNames"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Alternate Names"
							initialIsOpen={formState.formValues.alternateNames !== ""}
						>
							<PersonField.AlternateNames formState={formState} />
						</FieldToggle>
						<PersonField.Image formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Demographics
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<PersonField.Gender formState={formState} />
						<PersonField.DateOfBirth formState={formState} />
						<PersonField.Nationalities.Select formState={formState} />
						<PersonField.Languages.Select formState={formState} />
						<PersonField.EnglishLanguageFluency formState={formState} />
						<PersonField.Occupation formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Government ID
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<PersonField.NationalIdentificationNumber formState={formState} />
						<PersonField.GovernmentDocumentSeries formState={formState} />
						<PersonField.GovernmentDocumentNumber formState={formState} />
						<PersonField.GovernmentDocumentIssuedBy formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Contact
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column verticalSpacing="2rem">
						<Column justify="spaced-start" verticalSpacing="1rem">
							<AddressSameAsFormIdentifierField formState={formState} otherGuardians={otherGuardians} />
							<div
								style={
									formState.formValues.addressSameAsFormIdentifier !== null
										? { display: "none", visibility: "hidden" }
										: undefined
								}
							>
								{/* TODO set back to one address when "same-as" changed back to "New Address" (not done due to issues with subform states and form validation) */}
								<PersonField.Addresses.SubForm formState={formState} noOptional maxItems={1} />
							</div>
						</Column>
						<PersonField.EmailAddresses.SubForm formState={formState} noOptional />
						<PersonField.PhoneNumbers.SubForm formState={formState} noOptional />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Portal Access
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<PersonField.User.SubForm formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}

type AddressSameAsFormIdentifierFieldProps = {
	formState: FormState<Pick<GuardianCustomFormValues.Create["person"][0], "addressSameAsFormIdentifier">>;
	otherGuardians: GuardianCustomFormValues.Create[];
} & Partial<ClassNameProps & StyleProps>;

function AddressSameAsFormIdentifierField({
	formState,
	otherGuardians,
	className,
	style,
}: AddressSameAsFormIdentifierFieldProps) {
	const options = React.useMemo(() => {
		return [
			...filterMap(
				otherGuardians,
				(g) => g.person[0]?.addresses.length > 0,
				(g) => ({
					label: `Same as ${g.person[0].givenName} at ${g.person[0].addresses[0].addressLine1}`,
					value: g.formIdentifier,
				}),
			),
			{
				label: "New Address",
				value: null,
			},
		];
	}, [otherGuardians]);

	return (
		<FieldIf formState={formState} name="addressSameAsFormIdentifier" condition={options.length > 1}>
			<RadioField
				formState={formState}
				name="addressSameAsFormIdentifier"
				label="Address"
				options={options}
				optional
				noOptional
				className={className}
				style={style}
			/>
		</FieldIf>
	);
}

function RelationshipSubForm({
	formState,
	noHeader = true,
	style,
	...props
}: BaseFieldProps<Pick<GuardianCustomFormValues.Create, "relationships">>) {
	return (
		<SubFormField
			formState={formState}
			name="relationships"
			blankItem={RelationshipFormValues.blankItemInGuardian}
			minItems={1}
			maxItems={1}
			noHeader={noHeader}
			style={{ "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
			{...props}
		>
			{RelationshipSubForm.SubForm}
			{RelationshipSubForm.AddButton}
		</SubFormField>
	);
}

RelationshipSubForm.SubForm = function ({
	formState,
	onRemoveClick,
	canRemoveItem,
}: SubFormRenderProps<RelationshipFormValues.CreateInGuardian>) {
	return (
		<Form.Column>
			<If condition={canRemoveItem}>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Relationship
				</Button>
			</If>
			<RelationshipCustomCreateFieldsInGuardian formState={formState} />
		</Form.Column>
	);
};

RelationshipSubForm.AddButton = function ({
	onClick,
	disabled,
	canAddItem,
}: AddSubFormButtonRenderProps<
	Pick<GuardianCustomFormValues.Create, "relationships">,
	RelationshipFormValues.CreateInGuardian
>) {
	return (
		<If condition={canAddItem}>
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Relationship
			</Button>
		</If>
	);
};

const relationshipDetailsFieldNames: (keyof RelationshipFormValues.CreateInGuardian)[] = [
	"isPrimary",
	"isFinancialContact",
	"isEmergencyContact",
	"isAuthorizedForPickup",
	"livesWithStudent",
	"hasCustody",
];

type RelationshipCustomCreateFieldsInGuardianProps = {
	formState: FormState<RelationshipFormValues.CreateInGuardian>;
};

function RelationshipCustomCreateFieldsInGuardian({ formState }: RelationshipCustomCreateFieldsInGuardianProps) {
	useInternalField(formState, "studentID");

	return (
		<React.Fragment>
			<RelationshipField.RelationshipType formState={formState} />
			<FieldSet formState={formState} names={relationshipDetailsFieldNames} label="Relationship Details">
				<Form.Column verticalSpacing="0.25rem">
					<RelationshipField.IsPrimary formState={formState} />
					<RelationshipField.IsFinancialContact formState={formState} />
					<RelationshipField.IsEmergencyContact formState={formState} />
					<RelationshipField.IsAuthorizedForPickup formState={formState} />
					<RelationshipField.LivesWithStudent formState={formState} />
					<RelationshipField.HasCustody formState={formState} />
				</Form.Column>
			</FieldSet>
		</React.Fragment>
	);
}
